import { osName } from '@/constant/osName';
import {
  socialMediaData,
  viewPermissionValue,
} from '@/constant/user-setting-modal-resources';
import { checkNullish } from '@/helpers/checkNullish';
import { IUserProfileData, RoleViewEnum } from '@/interfaces/profile.interface';
import { saveAs } from 'file-saver';
import { removeRegionInS3PresignedURL } from './fileDownload';

/**All reference docs:
 * @vCard_Format_Specification
 * https://www.rfc-editor.org/rfc/rfc6350.html?fbclid=IwAR0ZQniqLuatgGL5WxikMt6HhFC1_Rox96UQ1fdg6iaUzyuJikDI9DOSLsA
 * @Wiki_vCard
 * https://en.wikipedia.org/wiki/VCard?fbclid=IwAR1ZRWbVBoxCn5-u87UIYBaLEM5EVgu441nxLnNnU5x-9oau7EtNVu1_SgE#Properties
 * @Reference_blog
 * https://alessandrorossini.org/the-sad-story-of-the-vcard-format-and-its-lack-of-interoperability/
 * @Type_Conventions_Mapping
 * https://docs.oracle.com/cd/E88140_01/books/ConnMobApps/connmobapps_carddavmap009.htm#CHDBJCFG
 * @Refernce_Repository
 * https://github.com/cozy/cozy-vcard/blob/master/test/android-full.vcf
 */
const createVCard = async (data: IUserProfileData) => {
  const vCardString = await generateVCardString(data, generateSocialString);
  var file = new Blob([vCardString], {
    type: 'text/vcard;charset=utf-8',
  });

  saveAs(file);
};

function generateSocialString(filteredSocial: any[]) {
  // AuthContext.tsx file
  const os = checkNullish(localStorage.getItem('os'));

  const hashSocialStringByOS = {
    [osName.Android]: `${filteredSocial
      .map(
        (item, index) =>
          `item${index + 1}.URL:${item.link}\nitem1.X-ABLabel:${
            item.socialNetwork === 17
              ? '_$!<Other>!$_'
              : `${socialMediaData[item.socialNetwork].name}`
          }`
      )
      .join('\n')}`,
    [osName.iOS]: `${filteredSocial
      .map((item, index) => {
        switch (item.socialNetwork) {
          case 17:
            return `item${index + 1}.URL:${
              item.link
            }\nitem1.X-ABLabel:_$!<Other>!$_`;
          default:
            return `X-SOCIALPROFILE;type=${
              socialMediaData[item.socialNetwork].value
            }:${item.link}`;
        }
      })
      .join('\n')}`,
    [osName.Windows]: `${filteredSocial
      .map(
        (item, index) =>
          `item${index + 1}.URL:${item.link}\nitem1.X-ABLabel:${
            item.socialNetwork === 17
              ? '_$!<Other>!$_'
              : `${socialMediaData[item.socialNetwork].name}`
          }`
      )
      .join('\n')}`,
  };

  return hashSocialStringByOS[os as keyof typeof osName];
}

async function generateVCardString(
  data: IUserProfileData,
  generateSocialString: (_: any[]) => string
) {
  const profiles = data.profiles;
  const { filteredContact, filteredSocial } =
    returnFilterdContactAndSocial(data);

  const avatar: string = !data.profiles.avatar
    ? ''
    : await new Promise<string>((resolve, _reject) => {
        fetch(removeRegionInS3PresignedURL(data.profiles.avatar || ''), {
          method: 'GET',
        })
          .then((res) => res.blob())
          .then(async (res) => {
            const blob = new Blob([res], {
              type: 'image/png',
            });

            const fileReader = new FileReader();
            fileReader.onload = (e) => resolve(e.target?.result as string);
            fileReader.readAsDataURL(blob);
          })
          .catch(() => {
            resolve('');
          });
      }).then((res) => res.replace('data:image/png;base64,', ''));

  return `BEGIN:VCARD
VERSION:3.0
N:${profiles.qbankData.fullName.split(' ').join(';')};;
FN:${profiles.qbankData.fullName}
PHOTO;ENCODING=b;TYPE=PNG:${avatar}
${
  /** Personal email */
  mapFilteredContactInfo((item) => item.contactType === 1, filteredContact)
    .map((item) => `EMAIL;type=INTERNET;type=HOME:${item}`)
    .join('\n')
}
${
  /** Work email */
  mapFilteredContactInfo((item) => item.contactType === 3, filteredContact)
    .map((item) => `EMAIL;type=INTERNET;type=WORK:${item}`)
    .join('\n')
}
${
  /** Personal phone */
  mapFilteredContactInfo((item) => item.contactType === 0, filteredContact)
    .map((item) => `TEL;type=CELL;type=VOICE:${item}`)
    .join('\n')
}
${
  /** Work phone */
  mapFilteredContactInfo((item) => item.contactType === 2, filteredContact)
    .map((item) => `TEL;type=WORK;type=VOICE:${item}`)
    .join('\n')
}
${generateSocialString(filteredSocial)}
END:VCARD`;
}

function filterDataDueToViewPermission(
  data: any[],
  viewPermission: RoleViewEnum
) {
  const filterViewPermission = {
    [viewPermissionValue.Public]: true, //every one can see content have viewPermissionValue === public
    [viewPermissionValue.Protected]: viewPermission !== RoleViewEnum.Guest, //only one who is follower can see content have viewPermissionValue === protected
    // the condition below cant be invoked in profile by themself
    // [viewPermissionValue.Private]: viewPermission === RoleViewEnum.Owner, //only owner can see content have viewPermissionValue === private
  };

  return data.filter(
    (item) =>
      filterViewPermission[
        item.viewPermission as keyof typeof viewPermissionValue
      ]
  );
}

function mapFilteredContactInfo(cb: (_: any) => boolean, array: any[]) {
  return array.filter(cb).map((item) => item.contactInfo);
}

function returnFilterdContactAndSocial(data: IUserProfileData) {
  const profiles = data.profiles;

  const filteredContact = filterDataDueToViewPermission(
    profiles.contact,
    data.viewPermission
  );

  const filteredSocial = filterDataDueToViewPermission(
    profiles.social,
    data.viewPermission
  );

  return { filteredContact, filteredSocial };
}

export default createVCard;
